import api from '../services/api';

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || '');
      if (!result.startsWith('data:')) {
        reject(new Error('Falha ao converter blob'));
        return;
      }
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const isSameOrigin = (url: string): boolean => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

const isProbablyImageBlob = (blob: Blob): boolean => {
  if (!blob || blob.size < 32) return false;
  if (blob.type.startsWith('image/')) return true;
  // Alguns proxies devolvem octet-stream
  if (!blob.type || blob.type === 'application/octet-stream') return true;
  return false;
};

const fetchAsDataUrl = async (fetchUrl: string): Promise<string | null> => {
  const res = await fetch(fetchUrl, {
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  if (!isProbablyImageBlob(blob)) return null;
  // Rejeita JSON de erro disfarçado
  if (blob.type.includes('json') || blob.type.includes('html')) return null;
  return blobToDataUrl(blob);
};

/**
 * Converte URL de imagem em data URL para o html-to-image embutir no PNG.
 * Ordem: mesma origem → proxy da API → fetch CORS no S3 → proxy público (weserv).
 * O proxy público é fallback temporário até o bucket S3 ter CORS liberado.
 */
export async function resolveImageDataUrl(url: string): Promise<string | null> {
  if (!url) return null;
  if (url.startsWith('data:')) return url;

  // 1) Mesma origem (logo /assets)
  if (isSameOrigin(url) || url.startsWith('/')) {
    try {
      const absolute = url.startsWith('/')
        ? `${window.location.origin}${url}`
        : url;
      const data = await fetchAsDataUrl(absolute);
      if (data) return data;
    } catch {
      /* continua */
    }
  }

  // 2) Proxy autenticado na API Laravel (melhor solução permanente no backend)
  // GET /api/tools/image-proxy?url=...
  try {
    const response = await api.get('/tools/image-proxy', {
      params: { url },
      responseType: 'blob',
      timeout: 20000,
    });
    const blob = response.data as Blob;
    if (isProbablyImageBlob(blob) && !blob.type.includes('json')) {
      return await blobToDataUrl(blob);
    }
  } catch {
    /* rota pode não existir ainda */
  }

  // 3) Fetch direto (funciona quando o S3 tiver CORS)
  try {
    const data = await fetchAsDataUrl(url);
    if (data) return data;
  } catch {
    /* CORS bloqueado — esperado hoje */
  }

  // 4) Proxy de imagem público (garante fotos no PNG agora)
  // wsrv.nl / images.weserv.nl — só para URLs públicas (S3 public/)
  try {
    const withoutProtocol = url.replace(/^https?:\/\//i, '');
    const candidates = [
      `https://wsrv.nl/?url=${encodeURIComponent(withoutProtocol)}&output=jpg&n=-1`,
      `https://images.weserv.nl/?url=${encodeURIComponent(withoutProtocol)}&output=jpg&n=-1`,
    ];
    for (const proxyUrl of candidates) {
      try {
        const data = await fetchAsDataUrl(proxyUrl);
        if (data) return data;
      } catch {
        /* tenta próximo */
      }
    }
  } catch {
    /* ignore */
  }

  return null;
}
